import { useCallback, useEffect, useState, useSyncExternalStore, type MouseEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { SearchInput, SuggestionsList } from "@boundary/components/searchInput";
import { ServiceSelect } from "@boundary/widgets/serviceSelect";
import { Page } from "@boundary/layouts/pageLayout";
import { useOrderFormState } from "@boundary/_hooks/useOrderFormState";
import { SuggestionsStore } from "@infrastructure/stores/suggestions.store";
import { getInfoByService } from "@domain/enums/local/getInfoByService";
import { useServiceMetadata } from "@boundary/_hooks/useServiceMetadata";
import { ServiceMetadataStore } from "@infrastructure/stores/serviceMetadata.store";
import { ServiceMetadataService } from "@domain/services/serviceMetadata.service";
import { Spinner } from "@boundary/components/spinner";
import { prefetchAndNavigate } from "@boundary/utils/prefetchRoute";
import styles from "./catalog.module.scss";

export function CatalogPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const machineIdParam = searchParams.get("machineId");
  const machineId = machineIdParam ? Number(machineIdParam) : 0;

  const vm = useOrderFormState(machineId);
  const { orderedServiceTypes, orderedTopLevelServiceTypes } = useServiceMetadata();
  const { suggestions } = useSyncExternalStore(
    SuggestionsStore.subscribe,
    SuggestionsStore.getState
  );
  const parentFromUrl = searchParams.get("parent");
  const [search, setSearch] = useState("");
  const [variantsParent, setVariantsParent] = useState<string | null>(parentFromUrl);
  const [variants, setVariants] = useState<string[]>([]);
  const [variantsLoading, setVariantsLoading] = useState(false);

  useEffect(() => {
    if (parentFromUrl && parentFromUrl !== variantsParent) {
      setVariantsParent(parentFromUrl);
    }
  }, [parentFromUrl]);

  const allowedServices = vm.supportedServiceTypes.length > 0
    ? vm.supportedServiceTypes
    : orderedServiceTypes;

  useEffect(() => {
    if (!variantsParent) {
      setVariants([]);
      return;
    }
    let cancelled = false;
    setVariantsLoading(true);
    void ServiceMetadataService.loadVariants(variantsParent).then((types) => {
      if (!cancelled) {
        setVariants(types);
        setVariantsLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, [variantsParent]);

  const goToService = useCallback((serviceType: string, fromParent?: string) => {
    const existingService = vm.services.find(s => s.type === serviceType);
    if (existingService) {
      vm.editServiceFromCart(existingService.id);
    } else {
      vm.createService(serviceType);
    }
    SuggestionsStore.addSuggestion(serviceType);
    const params = new URLSearchParams({ machineId: String(machineId), serviceType });
    if (fromParent) params.set("fromParent", fromParent);
    prefetchAndNavigate(navigate, `/service?${params}`);
  }, [vm, navigate, machineId]);

  const handleServiceClick = useCallback((serviceType: string) => {
    if (ServiceMetadataStore.getHasVariants(serviceType)) {
      setVariantsParent(serviceType);
      setSearch("");
      navigate(`/catalog?machineId=${machineId}&parent=${encodeURIComponent(serviceType)}`, { replace: true });
      return;
    }
    goToService(serviceType);
  }, [goToService, navigate, machineId]);

  const searchLower = search.toLowerCase().trim();
  const filteredServices = searchLower
    ? orderedServiceTypes.filter((st) =>
        getInfoByService(st).title.toLowerCase().includes(searchLower)
      )
    : orderedTopLevelServiceTypes;

  const filteredVariants = search
    ? variants.filter((v) => getInfoByService(v).title.toLowerCase().includes(search.toLowerCase()))
    : variants;

  const handleBackClick = useCallback((e?: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    if (e) { e.stopPropagation(); e.preventDefault(); }

    if (variantsParent) {
      setVariantsParent(null);
      navigate(`/catalog?machineId=${machineId}`, { replace: true });
      return;
    }

    if (vm.services.length > 0) {
      prefetchAndNavigate(navigate, `/cart?machineId=${machineId}`);
    } else {
      prefetchAndNavigate(navigate, `/map?machineId=${machineId}`);
    }
  }, [navigate, vm.services.length, machineId, variantsParent]);

  const parentInfo = variantsParent ? getInfoByService(variantsParent) : null;
  const showVariants = variantsParent !== null;

  return (
    <Page screenBackground="sub">
      <Page.SafeArea />
      <Page.DisplayZone>
        <Page.DisplayZone.Header
          back
          onBackClick={handleBackClick}
          center={
            showVariants
              ? <h1 className={styles.headerTitle}>{parentInfo?.title}</h1>
              : <SearchInput value={search} onChange={setSearch} />
          }
          fullCenter={!showVariants}
        />
        <Page.DisplayZone.Body className={styles.content} padded={false}>
          {!showVariants && (
            <div className={styles.suggestions}>
              <SuggestionsList
                suggestions={suggestions}
                onSuggestionClick={handleServiceClick}
              />
            </div>
          )}
          <div className={styles.serviceList}>
            {showVariants ? (
              variantsLoading ? (
                <div className={styles.spinnerContainer}><Spinner /></div>
              ) : (
                <ServiceSelect
                  items={filteredVariants}
                  allowedServices={filteredVariants}
                  isDisabled={(st) => !getInfoByService(st).enabled}
                  onClick={(st) => goToService(st, variantsParent ?? undefined)}
                />
              )
            ) : (
              <ServiceSelect
                items={filteredServices}
                allowedServices={allowedServices}
                isDisabled={(st) => !getInfoByService(st).enabled}
                onClick={handleServiceClick}
              />
            )}
          </div>
        </Page.DisplayZone.Body>
      </Page.DisplayZone>
    </Page>
  );
}
